import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  reload,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth'

import { getFirebaseServices } from '../../services/firebase'

export type DashboardUser = {
  uid: string
  email: string | null
  displayName?: string | null
  emailVerified: boolean
  getIdToken: (forceRefresh?: boolean) => Promise<string>
}

export type AuthAdapter = {
  subscribe: (callback: (user: DashboardUser | null) => void) => () => void
  signIn: (email: string, password: string) => Promise<void>
  sendPasswordReset?: (email: string, continueUrl?: string) => Promise<void>
  signUp: (email: string, password: string) => Promise<DashboardUser>
  sendEmailVerification: () => Promise<void>
  reloadCurrentUser: () => Promise<DashboardUser>
  signOut: () => Promise<void>
}

function requireCurrentUser() {
  const user = getFirebaseServices().auth.currentUser
  if (!user) throw new Error('Authenticated user is required')
  return user
}

export const firebaseAuthAdapter: AuthAdapter = {
  subscribe(callback) {
    return onAuthStateChanged(getFirebaseServices().auth, callback)
  },
  async signIn(email, password) {
    await signInWithEmailAndPassword(
      getFirebaseServices().auth,
      email,
      password
    )
  },
  async sendPasswordReset(email, continueUrl) {
    await sendPasswordResetEmail(
      getFirebaseServices().auth,
      email,
      continueUrl
        ? {
            url: continueUrl,
            handleCodeInApp: false,
          }
        : undefined
    )
  },
  async signUp(email, password) {
    const credential = await createUserWithEmailAndPassword(
      getFirebaseServices().auth,
      email,
      password
    )
    return credential.user
  },
  async sendEmailVerification() {
    await sendEmailVerification(requireCurrentUser())
  },
  async reloadCurrentUser() {
    const user = requireCurrentUser()
    await reload(user)
    return requireCurrentUser()
  },
  async signOut() {
    await signOut(getFirebaseServices().auth)
  },
}
